import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAdminAuth } from '@/hooks/useAdminAuth';
import { supabase } from '@/lib/supabase';

interface Article {
  id: string;
  title: string;
  slug: string;
  is_published: boolean;
  created_at: string;
  updated_at: string;
  category_id: string;
  article_type: string;
}

interface Category {
  id: string;
  name: string;
  slug: string;
}

function formatDate(dateStr: string) {
  const date = new Date(dateStr);
  return date.toLocaleDateString('zh-CN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
}

export default function AdminDashboard() {
  const { isAuthenticated, isChecking, logout } = useAdminAuth();
  const navigate = useNavigate();
  const [articles, setArticles] = useState<Article[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [categoryMap, setCategoryMap] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'published' | 'draft'>('all');

  useEffect(() => {
    if (!isChecking && !isAuthenticated) {
      navigate('/admin', { replace: true });
      return;
    }
    if (!isAuthenticated) return;

    async function fetchData() {
      try {
        const [articlesRes, categoriesRes] = await Promise.all([
          supabase
            .from('articles')
            .select('id, title, slug, is_published, created_at, updated_at, category_id, article_type')
            .order('created_at', { ascending: false }),
          supabase
            .from('categories')
            .select('id, name, slug')
            .order('sort_order', { ascending: true }),
        ]);

        if (articlesRes.error) throw articlesRes.error;
        if (categoriesRes.error) throw categoriesRes.error;

        setArticles(articlesRes.data || []);
        setCategories(categoriesRes.data || []);

        const map: Record<string, string> = {};
        (categoriesRes.data || []).forEach((c: Category) => {
          map[c.id] = c.name;
        });
        setCategoryMap(map);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load data');
      } finally {
        setLoading(false);
      }
    }

    fetchData();
  }, [isAuthenticated, isChecking, navigate]);

  const handleDelete = async (id: string) => {
    try {
      const { error: deleteError } = await supabase
        .from('articles')
        .delete()
        .eq('id', id);

      if (deleteError) throw deleteError;

      setArticles((prev) => prev.filter((a) => a.id !== id));
      setDeleteConfirm(null);
    } catch (err) {
      alert(err instanceof Error ? err.message : '删除失败');
    }
  };

  const handleTogglePublish = async (id: string, currentStatus: boolean) => {
    try {
      const { error: updateError } = await supabase
        .from('articles')
        .update({ is_published: !currentStatus, updated_at: new Date().toISOString() })
        .eq('id', id);

      if (updateError) throw updateError;

      setArticles((prev) =>
        prev.map((a) => (a.id === id ? { ...a, is_published: !currentStatus } : a))
      );
    } catch (err) {
      alert(err instanceof Error ? err.message : '操作失败');
    }
  };

  const filteredArticles = articles.filter((a) => {
    const matchesSearch = !searchQuery.trim() || a.title.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus =
      statusFilter === 'all' ||
      (statusFilter === 'published' && a.is_published) ||
      (statusFilter === 'draft' && !a.is_published);
    return matchesSearch && matchesStatus;
  });

  const publishedCount = articles.filter((a) => a.is_published).length;
  const draftCount = articles.filter((a) => !a.is_published).length;

  if (isChecking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background-50">
        <div className="w-6 h-6 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!isAuthenticated) return null;

  return (
    <main className="min-h-screen bg-background-100">
      {/* Header */}
      <header className="bg-background-50 border-b border-background-200/70 sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 md:px-8 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-primary-100 flex items-center justify-center">
              <i className="ri-shield-check-line text-primary-600 text-sm" />
            </div>
            <h1 className="font-heading text-base font-bold text-foreground-900">文章管理</h1>
          </div>
          <div className="flex items-center gap-2">
            <Link
              to="/admin/edit/new"
              className="bg-primary-500 hover:bg-primary-600 text-white px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors cursor-pointer inline-flex items-center gap-1.5"
            >
              <i className="ri-add-line text-sm" />
              新建文章
            </Link>
            <Link
              to="/admin/accounts"
              className="bg-background-100 hover:bg-background-200 text-foreground-600 px-3 py-2 rounded-lg text-sm whitespace-nowrap transition-colors cursor-pointer inline-flex items-center gap-1"
              title="帐号管理"
            >
              <i className="ri-user-settings-line text-sm" />
              帐号
            </Link>
            <button
              onClick={logout}
              className="bg-background-100 hover:bg-background-200 text-foreground-600 px-3 py-2 rounded-lg text-sm whitespace-nowrap transition-colors cursor-pointer inline-flex items-center gap-1"
            >
              <i className="ri-logout-box-r-line text-sm" />
              退出
            </button>
            <Link
              to="/"
              className="text-foreground-400 hover:text-foreground-600 px-2 py-2 rounded-lg text-sm whitespace-nowrap transition-colors cursor-pointer"
              title="返回网站"
            >
              <i className="ri-home-line" />
            </Link>
          </div>
        </div>
      </header>

      {/* Content */}
      <section className="max-w-6xl mx-auto px-4 md:px-8 py-6">
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 text-sm text-red-700 flex items-center gap-2">
            <i className="ri-error-warning-line" />
            {error}
            <button
              onClick={() => { setError(null); setLoading(true); window.location.reload(); }}
              className="ml-auto text-red-600 hover:text-red-800 underline cursor-pointer text-xs"
            >
              重试
            </button>
          </div>
        )}

        {loading ? (
          <div className="space-y-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="h-16 bg-background-200 rounded-lg animate-pulse" />
            ))}
          </div>
        ) : (
          <>
            {/* Search & Filter Bar */}
            <div className="flex flex-col sm:flex-row gap-3 mb-4">
              <div className="relative flex-1">
                <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="搜索文章标题..."
                  className="w-full pl-9 pr-4 py-2 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-foreground-400 hover:text-foreground-600 cursor-pointer"
                  >
                    <i className="ri-close-circle-line text-sm" />
                  </button>
                )}
              </div>
              <div className="flex gap-1 bg-background-100 rounded-lg p-1 self-start">
                {(['all', 'published', 'draft'] as const).map((status) => (
                  <button
                    key={status}
                    onClick={() => setStatusFilter(status)}
                    className={`px-3 py-1.5 rounded-md text-xs font-medium whitespace-nowrap transition-colors cursor-pointer ${
                      statusFilter === status
                        ? 'bg-background-50 text-foreground-900 shadow-sm'
                        : 'text-foreground-500 hover:text-foreground-700'
                    }`}
                  >
                    {status === 'all' ? '全部' : status === 'published' ? '已发布' : '草稿'}
                    {status !== 'all' && (
                      <span className="ml-1 text-foreground-400">
                        ({status === 'published' ? publishedCount : draftCount})
                      </span>
                    )}
                  </button>
                ))}
              </div>
            </div>

            {filteredArticles.length === 0 ? (
              <div className="text-center py-20">
                <div className="w-16 h-16 rounded-full bg-background-200 flex items-center justify-center mx-auto mb-4">
                  <i className="ri-article-line text-2xl text-foreground-400" />
                </div>
                <p className="text-foreground-500 text-sm mb-4">
                  {articles.length === 0 ? '还没有任何文章' : '没有匹配的文章'}
                </p>
                {articles.length === 0 && (
                  <Link
                    to="/admin/edit/new"
                    className="inline-flex items-center gap-1.5 bg-primary-500 hover:bg-primary-600 text-white px-5 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors cursor-pointer"
                  >
                    <i className="ri-add-line text-sm" />
                    创建第一篇文章
                  </Link>
                )}
              </div>
            ) : (
              <div className="bg-background-50 rounded-xl border border-background-200/70 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-background-200/70 bg-background-100/50">
                        <th className="text-left px-5 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider">
                          文章标题
                        </th>
                        <th className="text-left px-5 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider hidden sm:table-cell">
                          分类
                        </th>
                        <th className="text-left px-5 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider hidden md:table-cell">
                          类型
                        </th>
                        <th className="text-left px-5 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider hidden sm:table-cell">
                          状态
                        </th>
                        <th className="text-left px-5 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider hidden md:table-cell">
                          创建日期
                        </th>
                        <th className="text-right px-5 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider">
                          操作
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-background-200/50">
                      {filteredArticles.map((article) => (
                        <tr key={article.id} className="hover:bg-background-100/50 transition-colors">
                          <td className="px-5 py-3">
                            <div className="flex items-center gap-2">
                              {!article.is_published && (
                                <span className="w-1.5 h-1.5 rounded-full bg-amber-400 flex-shrink-0" />
                              )}
                              <span className="text-foreground-900 font-medium truncate max-w-[200px] sm:max-w-[300px]">
                                {article.title}
                              </span>
                            </div>
                          </td>
                          <td className="px-5 py-3 hidden sm:table-cell">
                            <span className="inline-block px-2 py-0.5 rounded-full text-xs font-medium bg-secondary-100 text-secondary-700 whitespace-nowrap">
                              {categoryMap[article.category_id] || '未分类'}
                            </span>
                          </td>
                          <td className="px-5 py-3 hidden md:table-cell">
                            <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${
                              article.article_type === 'embedded'
                                ? 'bg-accent-100 text-accent-700'
                                : 'bg-background-200 text-foreground-600'
                            }`}>
                              <i className={`${article.article_type === 'embedded' ? 'ri-link text-xs mr-0.5' : 'ri-edit-box-line text-xs mr-0.5'}`} />
                              {article.article_type === 'embedded' ? '引用' : '原创'}
                            </span>
                          </td>
                          <td className="px-5 py-3 hidden sm:table-cell">
                            <button
                              onClick={() => handleTogglePublish(article.id, article.is_published)}
                              className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium whitespace-nowrap cursor-pointer transition-colors ${
                                article.is_published
                                  ? 'bg-green-100 text-green-700 hover:bg-green-200'
                                  : 'bg-amber-100 text-amber-700 hover:bg-amber-200'
                              }`}
                            >
                              <i className={article.is_published ? 'ri-eye-line text-xs' : 'ri-eye-off-line text-xs'} />
                              {article.is_published ? '已发布' : '草稿'}
                            </button>
                          </td>
                          <td className="px-5 py-3 text-foreground-500 text-xs hidden md:table-cell">
                            {formatDate(article.created_at)}
                          </td>
                          <td className="px-5 py-3 text-right">
                            <div className="flex items-center justify-end gap-1">
                              <Link
                                to={`/article/${article.slug}`}
                                target="_blank"
                                className="p-2 rounded-lg text-foreground-400 hover:text-primary-600 hover:bg-primary-50 transition-colors cursor-pointer"
                                title="预览"
                              >
                                <i className="ri-external-link-line text-sm" />
                              </Link>
                              <Link
                                to={`/admin/edit/${article.slug}`}
                                className="p-2 rounded-lg text-foreground-400 hover:text-foreground-700 hover:bg-background-200 transition-colors cursor-pointer"
                                title="编辑"
                              >
                                <i className="ri-edit-line text-sm" />
                              </Link>
                              {deleteConfirm === article.id ? (
                                <div className="flex items-center gap-1">
                                  <button
                                    onClick={() => handleDelete(article.id)}
                                    className="px-2 py-1 rounded text-xs font-medium bg-red-500 text-white hover:bg-red-600 cursor-pointer whitespace-nowrap"
                                  >
                                    确认
                                  </button>
                                  <button
                                    onClick={() => setDeleteConfirm(null)}
                                    className="px-2 py-1 rounded text-xs font-medium bg-background-200 text-foreground-600 hover:bg-background-300 cursor-pointer whitespace-nowrap"
                                  >
                                    取消
                                  </button>
                                </div>
                              ) : (
                                <button
                                  onClick={() => setDeleteConfirm(article.id)}
                                  className="p-2 rounded-lg text-foreground-400 hover:text-red-500 hover:bg-red-50 transition-colors cursor-pointer"
                                  title="删除"
                                >
                                  <i className="ri-delete-bin-line text-sm" />
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}

        {/* Stats */}
        <div className="mt-6 flex flex-wrap gap-4">
          <div className="bg-background-50 rounded-xl border border-background-200/70 px-5 py-4">
            <p className="text-xs text-foreground-500 mb-1">文章总数</p>
            <p className="font-heading text-2xl font-bold text-foreground-900">{articles.length}</p>
          </div>
          <div className="bg-background-50 rounded-xl border border-background-200/70 px-5 py-4">
            <p className="text-xs text-foreground-500 mb-1">已发布</p>
            <p className="font-heading text-2xl font-bold text-green-600">
              {publishedCount}
            </p>
          </div>
          <div className="bg-background-50 rounded-xl border border-background-200/70 px-5 py-4">
            <p className="text-xs text-foreground-500 mb-1">草稿</p>
            <p className="font-heading text-2xl font-bold text-amber-500">
              {draftCount}
            </p>
          </div>
          <div className="bg-background-50 rounded-xl border border-background-200/70 px-5 py-4">
            <p className="text-xs text-foreground-500 mb-1">分类数量</p>
            <p className="font-heading text-2xl font-bold text-foreground-900">{categories.length}</p>
          </div>
        </div>
      </section>
    </main>
  );
}